// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

export const getSalaries = createAsyncThunk('appSalaries/getSalaries', async (includeInactive = false) => {
  const response = await axios.get(`/v1/salaries`, { params: { includeInactive } })
  return response.data
})

export const getCustomers = createAsyncThunk('appSalaries/getCustomers', async () => {
    const response = await axios.get(`/v1/config/customers`)
    return response.data
})

// Rejects with the server's message (e.g. a 400 "Month must be MM-yyyy", or
// a 500 when the audit could not be written and nothing was saved).
export const updateSalary = createAsyncThunk('appSalaries/updateSalary', async ({userId, month, value, currency}, { rejectWithValue }) => {
  try {
    const response = await axios.post(`/v1/salaries`, { userId, month, value, currency })
    return { userId, data: response.data }
  } catch (error) {
    const serverMessage = error.response && error.response.data && error.response.data.message
    return rejectWithValue(serverMessage || error.message || 'Could not save the salary.')
  }
})

export const toggleSalaryHidden = createAsyncThunk('appSalaries/toggleSalaryHidden', async ({userId, salaryHidden}) => {
  const response = await axios.post(`/v1/salaries/toggle-hidden`, { userId, salaryHidden })
  return { userId, salaryHidden }
})

// ============================================================================
// Project Override Actions
// ============================================================================

export const getSalaryOverrides = createAsyncThunk('appSalaries/getSalaryOverrides', async ({userId, month}) => {
  const response = await axios.get(`/v1/salaries/overrides`, { params: { userId, month } })
  return response.data
})

export const createSalaryOverride = createAsyncThunk('appSalaries/createSalaryOverride', async ({userId, projectId, amount, effectiveFrom, currency}) => {
  const response = await axios.post(`/v1/salaries/overrides`, { userId, projectId, amount, effectiveFrom, currency })
  return response.data
})

export const updateSalaryOverride = createAsyncThunk('appSalaries/updateSalaryOverride', async ({overrideId, amount, effectiveFrom, currency}) => {
  const response = await axios.put(`/v1/salaries/overrides/${overrideId}`, { amount, effectiveFrom, currency })
  return response.data
})

export const deleteSalaryOverride = createAsyncThunk('appSalaries/deleteSalaryOverride', async ({overrideId}) => {
  const response = await axios.delete(`/v1/salaries/overrides/${overrideId}`)
  return { overrideId, ...response.data }
})

export const getSalaryAuditHistory = createAsyncThunk('appSalaries/getSalaryAuditHistory', async ({userId}) => {
  const response = await axios.get(`/v1/salaries/audit/${userId}`)
  return response.data
})

export const appSalariesSlice = createSlice({
  name: 'salaries',
  initialState: {
    data: [],
    customers: [],
    allMonths: [],
    isLoading: false,
    selectedOverrides: null, // For detail panel
    overridesLoading: false,
    auditHistory: [],
    auditLoading: false
  },
  reducers: {
    clearSelectedOverrides: (state) => {
      state.selectedOverrides = null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getSalaries.pending, (state) => {
        state.isLoading = true
        state.data = []
        state.allMonths = []
      })
      .addCase(getSalaries.fulfilled, (state, action) => {
        state.data = action.payload.users
        state.allMonths = action.payload.months
        state.isLoading = false
      })
      .addCase(updateSalary.fulfilled, (state, action) => {
          const userData = state.data.find(u => u.id === action.payload.userId)
          userData.salaries = action.payload.data
      })
      .addCase(toggleSalaryHidden.fulfilled, (state, action) => {
          const userData = state.data.find(u => u.id === action.payload.userId)
          userData.salaryHidden = action.payload.salaryHidden
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        state.customers = action.payload
      })
      // Override actions
      .addCase(getSalaryOverrides.pending, (state) => {
        state.overridesLoading = true
      })
      .addCase(getSalaryOverrides.fulfilled, (state, action) => {
        state.selectedOverrides = action.payload
        state.overridesLoading = false
      })
      .addCase(getSalaryOverrides.rejected, (state) => {
        state.overridesLoading = false
      })
      // Audit history
      .addCase(getSalaryAuditHistory.pending, (state) => {
        state.auditLoading = true
      })
      .addCase(getSalaryAuditHistory.fulfilled, (state, action) => {
        state.auditHistory = action.payload
        state.auditLoading = false
      })
      .addCase(getSalaryAuditHistory.rejected, (state) => {
        state.auditLoading = false
      })
  }
})

export const { clearSelectedOverrides } = appSalariesSlice.actions

export default appSalariesSlice.reducer
